import { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import type { KLineConfig, KLinePoint } from '../../types'
import { calculateBBI } from '../../utils/klineGenerator'

interface KLineChartProps {
  config: KLineConfig
  height?: number
}

/** 简单移动平均 */
function calcMA(data: KLinePoint[], period: number): (number | null)[] {
  return data.map((_, i) => {
    if (i < period - 1) return null
    const slice = data.slice(i - period + 1, i + 1)
    const sum = slice.reduce((acc, p) => acc + p.close, 0)
    return sum / period
  })
}

/** 从 CSS 变量读色（自动跟随主题） */
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

function readThemeColors() {
  return {
    grid: cssVar('--color-chart-grid', '#f4d4e4'),
    axis: cssVar('--color-chart-axis', '#a896b0'),
    text: cssVar('--color-chart-text', '#6b5b73'),
    bg1: cssVar('--color-chart-bg-1', 'rgba(254,247,245,0.5)'),
    bg2: cssVar('--color-chart-bg-2', 'rgba(253,242,248,0.5)'),
    cross: cssVar('--color-chart-cross', '#f9a8d4'),
    bull: cssVar('--color-bull', '#ff9ec6'),
    bear: cssVar('--color-bear', '#8fd9b6'),
    volUp: cssVar('--color-chart-vol-up', '#ffb7c8'),
    volDown: cssVar('--color-chart-vol-down', '#b5e5c9'),
    ma5: cssVar('--color-ma5', '#c084fc'),
    ma10: cssVar('--color-ma10', '#38bdf8'),
    ma20: cssVar('--color-ma20', '#fb923c'),
    gold: cssVar('--color-gold', '#fbd38d'),
    cyan: cssVar('--color-cyan', '#a5b4fc'),
    warning: cssVar('--color-warning', '#ffc78a'),
  }
}

function KLineChart({ config, height = 260 }: KLineChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  // ========== 初始化 ECharts + ResizeObserver ==========
  useEffect(() => {
    if (!chartRef.current) return
    chartInstance.current = echarts.init(chartRef.current)

    const ro = new ResizeObserver(() => chartInstance.current?.resize())
    ro.observe(chartRef.current)

    return () => {
      ro.disconnect()
      chartInstance.current?.dispose()
    }
  }, [])

  // ========== 主题变化时重绘 ==========
  useEffect(() => {
    if (!chartInstance.current) return
    const observer = new MutationObserver(() => {
      // 主题切换时重新触发渲染
      if (chartInstance.current && config.data) {
        renderOption()
      }
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  // ========== 渲染主函数（data/config 变化或主题变化时调用） ==========
  function renderOption() {
    if (!chartInstance.current || !config.data) return
    const data: KLinePoint[] = config.data
    const theme = readThemeColors()
    const dates = data.map((d: KLinePoint) => `D${d.day}`)
    const ohlc = data.map((d: KLinePoint) => [d.open, d.close, d.low, d.high])
    const volumes = data.map((d: KLinePoint) => d.volume)
    const ma5 = calcMA(data, 5)
    const ma10 = calcMA(data, 10)
    const ma20 = calcMA(data, 20)
    const bbi = config.indicators?.includes('bbi') ? calculateBBI(data) : null

    // X 轴只在首/尾/中点显示标签（避免拥挤）
    const xAxisLabelInterval = Math.max(0, Math.floor(data.length / 6) - 1)

    const option: echarts.EChartsOption = {
      animation: false,
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          lineStyle: { color: theme.cross, type: 'dashed', opacity: 0.7 },
          crossStyle: { color: theme.cross, type: 'dashed', opacity: 0.5 },
        },
        backgroundColor: 'rgba(255,255,255,0.96)',
        borderColor: theme.grid,
        borderWidth: 1,
        textStyle: { color: theme.text, fontSize: 12, fontFamily: 'VT323, monospace' },
        padding: [6, 8],
        extraCssText: 'box-shadow: 2px 2px 0 rgba(0,0,0,0.1);',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ECharts tooltip formatter 参数为弱类型联合
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return ''
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 同上，K 线 series 回调参数
          const candle = params.find((p: any) => p.seriesType === 'candlestick')
          if (!candle) return ''
          const i = candle.dataIndex
          const d = data[i]
          if (!d) return ''
          const open = d.open.toFixed(2)
          const close = d.close.toFixed(2)
          const high = d.high.toFixed(2)
          const low = d.low.toFixed(2)
          const change = (((d.close - d.open) / d.open) * 100).toFixed(2)
          const vol = d.volume >= 10000 ? `${(d.volume / 10000).toFixed(1)}万` : d.volume.toString()
          const changeColor = d.close >= d.open ? theme.bull : theme.bear
          const sign = d.close >= d.open ? '+' : ''
          const ma5v = ma5[i]?.toFixed(2) ?? '—'
          const ma10v = ma10[i]?.toFixed(2) ?? '—'
          const ma20v = ma20[i]?.toFixed(2) ?? '—'
          return `
            <div style="font-family:VT323,monospace;min-width:160px;line-height:1.5">
              <div style="font-weight:bold;color:${theme.text};border-bottom:1px dashed ${theme.grid};padding-bottom:2px;margin-bottom:4px">▸ Day ${d.day}</div>
              <div>开盘 <span style="float:right;color:${theme.text}">${open}</span></div>
              <div>收盘 <span style="float:right;color:${theme.text}">${close}</span></div>
              <div>最高 <span style="float:right;color:${theme.bull}">${high}</span></div>
              <div>最低 <span style="float:right;color:${theme.bear}">${low}</span></div>
              <div style="color:${changeColor}">涨跌 <span style="float:right">${sign}${change}%</span></div>
              <div>成交量 <span style="float:right;color:${theme.cyan}">${vol}</span></div>
              <div style="margin-top:4px;padding-top:4px;border-top:1px dashed ${theme.grid};font-size:11px">
                <span style="color:${theme.ma5}">MA5:${ma5v}</span>  <span style="color:${theme.ma10}">MA10:${ma10v}</span>  <span style="color:${theme.ma20}">MA20:${ma20v}</span>
              </div>
            </div>
          `
        },
      },
      grid: [
        { left: '4%', right: '4%', top: '8%', height: '60%' },
        { left: '4%', right: '4%', top: '74%', height: '16%' },
      ],
      xAxis: [
        {
          type: 'category',
          data: dates,
          boundaryGap: true,
          axisLine: { onZero: false, lineStyle: { color: theme.axis } },
          axisTick: { show: false },
          axisLabel: {
            color: theme.text,
            fontSize: 10,
            fontFamily: 'VT323',
            interval: xAxisLabelInterval,
          },
          splitLine: { show: false },
        },
        {
          type: 'category',
          gridIndex: 1,
          data: dates,
          boundaryGap: true,
          axisLine: { onZero: false, lineStyle: { color: theme.axis } },
          axisTick: { show: false },
          axisLabel: { show: false },
          splitLine: { show: false },
        },
      ],
      yAxis: [
        {
          scale: true,
          position: 'right',
          axisLabel: {
            color: theme.text,
            fontSize: 10,
            fontFamily: 'VT323',
            formatter: (v: number) => v.toFixed(2),
            inside: false,
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: theme.grid, type: 'dashed', opacity: 0.6 } },
          splitArea: { show: true, areaStyle: { color: [theme.bg1, theme.bg2] } },
        },
        {
          scale: true,
          gridIndex: 1,
          splitNumber: 2,
          position: 'right',
          axisLabel: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
        },
      ],
      series: [
        // 主 K 线
        {
          name: 'K线',
          type: 'candlestick',
          data: ohlc,
          itemStyle: {
            color: theme.bull,
            color0: theme.bear,
            borderColor: theme.bull,
            borderColor0: theme.bear,
          },
        },
        // MA5
        {
          name: 'MA5',
          type: 'line',
          data: ma5,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: theme.ma5, width: 1.2 },
          z: 3,
        },
        // MA10
        {
          name: 'MA10',
          type: 'line',
          data: ma10,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: theme.ma10, width: 1.2 },
          z: 3,
        },
        // MA20
        {
          name: 'MA20',
          type: 'line',
          data: ma20,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: theme.ma20, width: 1.2 },
          z: 3,
        },
        // BBI (多空指标)
        ...(bbi
          ? [
              {
                name: 'BBI',
                type: 'line' as const,
                data: bbi.map(v => (isNaN(v) ? null : v)),
                smooth: true,
                symbol: 'none' as const,
                lineStyle: { color: theme.gold, width: 1.5, type: 'dashed' as const },
                z: 2,
              },
            ]
          : []),
        // 成交量
        {
          name: '成交量',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: volumes,
          itemStyle: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ECharts itemStyle.color 回调参数为弱类型
            color: (params: any) => {
              const kline = data[params.dataIndex]
              if (!kline) return theme.volUp
              return kline.close >= kline.open ? theme.volUp : theme.volDown
            },
            borderRadius: [1, 1, 0, 0],
          },
          barWidth: '60%',
        },
      ],
    }

    // 标注（买卖点）
    if (config.markers && config.markers.length > 0) {
      const markData = config.markers.map((m: { day: number; type: string; label: string }) => ({
        name: m.label,
        coord: [`D${m.day}`, data.find((d: KLinePoint) => d.day === m.day)?.high ?? 0],
        value: m.label,
        itemStyle: {
          color: m.type === 'buy' ? theme.bull : m.type === 'sell' ? theme.bear : theme.warning,
        },
      }))

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- EChartsOption.series 为复杂联合类型，此处需可变访问
      const series = option.series as any[]
      series[0].markPoint = {
        symbol: 'pin',
        symbolSize: 38,
        data: markData,
        label: {
          color: '#fff',
          fontSize: 9,
          fontFamily: 'VT323',
          fontWeight: 'bold',
        },
      }
    }

    chartInstance.current.setOption(option, true)
  }

  // ========== data/config 变化时渲染 ==========
  useEffect(() => {
    renderOption()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  return <div ref={chartRef} style={{ width: '100%', height }} />
}

export default KLineChart